import { Formik, Form } from 'formik';
import { FormikInput, FormikSelect, PrimaryButton, SecondaryButton } from "../UI";
import PrimaryModal from '../UI/Modal/PrimaryModal';
import { useState } from 'react';
import { createOrganization, editOrganization } from '../../utils/APIs';
import { createOrganizationSchema } from '../../schemas';
import { toast } from 'react-toastify';
import { useUserContext } from '../../contexts/UserContext';
import Loader from '../UI/Loader/Loader';
import { useContentfulContext } from '../../contexts/ContentfulContext';

const OrganizationModal = ({orgLevels, setSelectedAction, selectedOrganization, getOrganizations }) => {
    const [submitError, setSubmitError] = useState('');
    const [loading, setLoading] = useState(false);
    const { userData } = useUserContext()
    const { content } = useContentfulContext();

    const isEdit = Boolean(selectedOrganization);

    const initialValues = {
        orgName: selectedOrganization?.name || '',
        displayName: selectedOrganization?.display_name || '',
        level: selectedOrganization?.level || '',
    };

    const createOrg = async (payload, actions) => {
        try {
            setLoading(true)
            const result = await createOrganization(userData.token, payload);
            if(result.status){
                toast.success(content.ORGANIZATION.success.create);
                getOrganizations()
            }
            actions.resetForm();
            setSubmitError('');
            setSelectedAction("");
        } catch (error) {
            const errMessage = error.message || content.ORGANIZATION.error.create;
            setSubmitError(errMessage);
            toast.error(errMessage);
            console.error('Create organization error:', error);
            actions.setSubmitting(false);
        } finally {
            setLoading(false)
        }
    };

    const editOrg = async (payload, actions) => {
        try {
            setLoading(true)
            const result = await editOrganization(userData.token, selectedOrganization.org_token, payload);
            if(result.status){
                toast.success(content.ORGANIZATION.success.update);
                getOrganizations()
            }
            actions.resetForm();
            setSubmitError('');
            setSelectedAction("");
        } catch (error) {
            const errMessage = error.message || content.ORGANIZATION.error.create
            setSubmitError(errMessage);
            toast.error(errMessage);
            console.error('Edit organization error:', error);
            actions.setSubmitting(false);
        } finally {
            setLoading(false)
        }
    };

    const handleSubmit = async (values, actions) => {
        const payload = {
            name : values.orgName,
            display_name : values.displayName,
            level : values.level,
        }
        if (isEdit) {
            await editOrg(payload, actions);
        } else {
            await createOrg(payload, actions);
        }
    };

    return (
        <PrimaryModal
            title={isEdit ? content.ORGANIZATION.edit_organization : content.ORGANIZATION.create_organization}
            hideModal={setSelectedAction}
        >
            {loading && <Loader />}
            <Formik
                initialValues={initialValues}
                validationSchema={() => createOrganizationSchema(content.ORGANIZATION)}
                onSubmit={handleSubmit}
            >
                {({ isSubmitting }) => (
                    <Form className="space-y-4 p-6">
                        <FormikInput
                            id="orgName"
                            name="orgName"
                            label={content.ORGANIZATION.org_name}
                            placeholder={content.ORGANIZATION.org_name}
                            mandatory
                        />

                        <FormikInput
                            id="displayName"
                            name="displayName"
                            label={content.ORGANIZATION.display_name}
                            placeholder={content.ORGANIZATION.display_name}
                            mandatory
                        />

                        <FormikSelect
                            id="level"
                            name="level"
                            label="Level"
                            placeholder={content.ORGANIZATION.select_level}
                            options={orgLevels.filter((l) => l.id != "0").map((level) => ({
                                value: level.id,
                                label: level.id,
                            }))}
                            mandatory
                        />
                        {submitError && (
                            <div className="mt-2 text-sm text-red-500">{submitError}</div>
                        )}
                        <div className="flex justify-end space-x-4">
                            <SecondaryButton handleClick={() => setSelectedAction("")}>
                                {content.ORGANIZATION.cancel}
                            </SecondaryButton>
                            <PrimaryButton type="submit" disabled={isSubmitting}>
                                {isEdit ? content.ORGANIZATION.edit_organization : content.ORGANIZATION.create_organization}
                            </PrimaryButton>
                        </div>
                    </Form>
                )}
            </Formik>
        </PrimaryModal>
    );
};

export default OrganizationModal;
